import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Box, Collapse, IconButton, TextField, Typography, Button } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../hooks/useAuth';
import useTTS from '../../hooks/useTTS';
import { formatEventForTTS } from '../../utils/ttsFormatter';
import Map from '../Map/Map';
import MobileMenuPanel from './MobileMenuPanel';
import BottomNavigation from './BottomNavigation';
import SmartEventList from '../SmartEventList/SmartEventList';
import ReviewerPanel from '../Reviewers/ReviewerPanel';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SavedItemsGrid from '../Dashboard/UserDashboard/SavedItemsGrid';
import FollowedReviewersList from '../Dashboard/UserDashboard/FollowedReviewersList';
import { useSavedPanel } from '../../hooks/useSavedPanel';
import SavedItemDetail from '../Dashboard/UserDashboard/SavedItemDetail';
import MyWorksPanel from '../Dashboard/UserDashboard/MyWorksPanel';
import MenuIcon from '@mui/icons-material/Menu';
import SearchIcon from '@mui/icons-material/Search';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import ExploreIcon from '@mui/icons-material/Explore';
import AppsIcon from '@mui/icons-material/Apps';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import { NAV_TABS } from '../../utils/categoriesConfig';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import CloseIcon from '@mui/icons-material/Close';
import TTSButton from './TTSButton';

const MobileLayout = ({
  coordinates,
  setCoordinates,
  bounds,
  setBounds,
  childClicked,
  setChildClicked,
  handleMarkerClick,
  handleLocationClick,
  currentView,
  setCurrentView,
  type,
  setType,
  subCategory,
  setSubCategory,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  searchTerm,
  setSearchTerm,
  isSearchExpanded,
  setIsSearchExpanded,
  followList,
  setFollowList,
  userLocation,
  feedLat,
  feedLng,
  feedRadiusKm,
  onSearch,
  searchQuery, // NEW: Search query to pass to SmartEventList
  hasSearched,
  source,              // NEW: Which collections to search
  excludeCategories,   // NEW: Categories to exclude
  categoryFilter,      // NEW: Category filter from tab config (resolved, for SmartEventList)
  selectedCreator,
  setSelectedCreator,
  creatorProfile,
  onCreatorClick,
  onReviewerSelect,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { language } = useLanguage();
  const { isAuthenticated, logout } = useAuth();
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showSavedPanel, setShowSavedPanel] = useState(false);
  const [showProfilePanel, setShowProfilePanel] = useState(false);
  // ผลงานของฉัน view toggle: 'grid' shows the works grid, 'map' reveals the map
  const [profileView, setProfileView] = useState('grid');

  const { speak, stop, isSupported, availableVoices, selectedVoiceName, setVoice, audioOutputDevices, selectedOutputDeviceId, setOutputDevice, readingFinished } = useTTS(language);

  // NEW: State to receive items from SmartEventList
  const [displayedItems, setDisplayedItems] = useState([]);

  // ผู้รีวิว tab renders its own panel (reviewer directory + that reviewer's items)
  const isReviewerTab = type === 'Reviewers';

  // แผนที่ view: collapse/expand the "what's interesting now" sheet
  const [isSheetOpen, setIsSheetOpen] = useState(true);
  // รายการ view: let the list cover the map strip
  const [isListExpanded, setIsListExpanded] = useState(true);

  // Saved panel (ตาราง / แผนที่ / ติดตาม) — shared logic with the desktop layout
  const savedPanel = useSavedPanel({ isAuthenticated, language, active: showSavedPanel });
  const { savedTab, setSavedTab } = savedPanel;
  // Map-tab only: drives the transparent, click-through panel + hidden chips.
  const savedMapActive = showSavedPanel && savedTab === 'map';
  // Grid OR map tab: the saved/reviewer items should appear as markers on the
  // shared map (the grid tab shows them too, e.g. beside the desktop grid).
  const savedShowMarkers = showSavedPanel && savedTab !== 'following';

  // ผลงานของฉัน: markers for the currently selected category tab.
  const [myWorksMapItems, setMyWorksMapItems] = useState([]);
  const [myWorksFitKey, setMyWorksFitKey] = useState(null);
  const handleMyWorksMapItems = useCallback((items, fitKey) => {
    setMyWorksMapItems(items || []);
    setMyWorksFitKey(fitKey || null);
  }, []);
  const profileMarkersActive = showProfilePanel && myWorksMapItems.length > 0;
  // Any transparent map-only overlay (saved map tab or ผลงานของฉัน map view):
  // the base category chips + recommendation sheet must be hidden underneath.
  const mapOnlyView = savedMapActive || (showProfilePanel && profileView === 'map');

  // Tapping a grid preview centers the shared map on that item, and shows the
  // map tab so the user sees where it is.
  const handleSavedGridItemClick = useCallback((item) => {
    // Pan the shared map toward the item (harmless), then show its detail inline.
    const lat = Number(item?.latitude);
    const lng = Number(item?.longitude);
    if (Number.isFinite(lat) && Number.isFinite(lng)) setCoordinates({ lat, lng });
    savedPanel.openDetail(item);
  }, [setCoordinates, savedPanel]);

  // A logged-out user tapping Saved / My Works is sent to login; we remember
  // which panel they pressed so we can reopen it here after they log in.
  const goToLoginForPanel = useCallback((panel) => {
    try { localStorage.setItem('returnUrl', `${window.location.pathname}?panel=${panel}`); } catch (e) { /* ignore */ }
    // Open login as an overlay over the still-mounted map page so closing it
    // returns here with all state intact.
    navigate('/login', { state: { background: location } });
  }, [navigate, location]);

  // Coming back from login with ?panel= → open that panel, then clean the URL.
  useEffect(() => {
    if (!isAuthenticated) return;
    const params = new URLSearchParams(location.search);
    const panel = params.get('panel');
    if (!panel) return;
    if (panel === 'saved') { setShowSavedPanel(true); setShowProfilePanel(false); }
    else if (panel === 'profile') { setShowProfilePanel(true); setShowSavedPanel(false); }
    params.delete('panel');
    const search = params.toString();
    navigate({ pathname: location.pathname, search: search ? `?${search}` : '' }, { replace: true });
  }, [isAuthenticated, location.search, location.pathname, navigate]);

  // Track whether TTS auto-read is enabled (toggled by the button)
  const [isTTSAutoEnabled, setIsTTSAutoEnabled] = useState(false);
  const previousChildClickedRef = useRef(childClicked);

  // NEW: State to preserve event ID when switching between vertical/horizontal list
  const [, setPreservedEventId] = useState(null);

  // NEW: State for expanded place
  const [expandedPlace, setExpandedPlace] = useState(null);

  const handleMobileMenuNavigate = (path) => {
    setShowMobileMenu(false);
    if (path === '/tour-guide') {
      navigate(path, { state: { background: location } });
    } else {
      navigate(path);
    }
  };

  // NEW: Handle info box click to switch tabs and expand item details
  const handleInfoBoxClick = useCallback((place) => {
    // 1. Move to the Explore navigation tab (vertical list view)
    setCurrentView('verticalExplore');
    
    // 2. Find the index in displayedItems to highlight and scroll
    const idx = displayedItems.findIndex(
      (it) => (it.event_id || it.id) === (place.event_id || place.id)
    );
    if (idx !== -1) {
      setChildClicked(idx);
    }
    
    // 3. Show details in expanded mode
    setExpandedPlace(place);
  }, [displayedItems, setCurrentView, setChildClicked]);

  // Helper function to normalize id field (some items use event_id, some use id)
  const getItemId = useCallback((item) => {
    return item?.event_id || item?.id || null;
  }, []);

  // NEW: Handle items updates from SmartEventList
  // Enhanced to restore focus after switching between vertical/horizontal list
  const handleItemsChange = useCallback(
    (items) => {
      // 1. update our local cache of currently shown items
      setDisplayedItems(items);

      // 2. if we were in the middle of a list-style switch, try to restore
      setPreservedEventId((prevId) => {
        if (!prevId) return null;

        const foundIndex = items.findIndex(
          (it) => getItemId(it) === prevId
        );

        if (foundIndex !== -1) {
          // tell parent / state that this is the newly focused item
          setChildClicked(foundIndex);
          // we consumed the preserved id
          return null;
        }

        // if the item is not in this window yet (because of batching),
        // keep the id for the next onItemsChange
        return prevId;
      });
    },
    [getItemId, setChildClicked]
  );

  // NEW: Handle map sync when active item changes in list
  // NOTE: Does NOT set childClicked to prevent feedback loop
  // (onActiveIndexChange already reports the index separately)
  const handleMapSync = useCallback((lat, lng, index) => {
    // Don't let list items far from the user's GPS location override the map center.
    // 2 degrees ≈ 220 km — items further than that won't re-center the map.
    if (userLocation) {
      const dLat = Math.abs(lat - userLocation.lat);
      const dLng = Math.abs(lng - userLocation.lng);
      if (dLat > 2 || dLng > 2) return;
    }
    setCoordinates({ lat, lng });
  }, [setCoordinates, userLocation]);

  // Handler for when active index changes in both lists (kept for compatibility)
  // STEP 6: Ignore active index changes while stabilizing
  const handleActiveIndexChange = useCallback((index, isStabilizing = false, isAuthoritative = false) => {
    if (isStabilizing) {
      // we are in the middle of a window shift → DO NOT update map, DO NOT set childClicked
      console.log('[MobileLayout] (ignored) active index', index, 'because list is stabilizing');
      return;
    }

    // extra guard: sometimes right after shift we get index 0 from a half-baked window
    if (index === 0 && !isAuthoritative) {
      console.log('[MobileLayout] (ignored) weak index 0 after shift');
      return;
    }

    const place = displayedItems[index];
    if (!place) return;

    // select in list
    setChildClicked(index);

    // move map to this event
    setCoordinates({
      lat: place.latitude,
      lng: place.longitude,
    });
  }, [displayedItems, setChildClicked, setCoordinates]);

  // Get selected event ID from childClicked index
  const selectedEventId = childClicked !== null && displayedItems[childClicked]
    ? (displayedItems[childClicked].event_id || displayedItems[childClicked].id)
    : null;
  const isVerticalExplore = currentView === 'verticalExplore';
  const recommendationSheetHeight = 270;
  const bottomNavigationContentHeight = 60;
  const visibleExploreMapHeight = 'var(--visible-explore-map-height)';
  const sheetHeaderHeight = 34;   // title row that stays visible when collapsed
  const listToggleHeight = 26;    // expand/collapse bar at the bottom of the list
  const mapControlsBottom = isVerticalExplore ? 16 : recommendationSheetHeight + bottomNavigationContentHeight + 70;
  const headerRef = useRef(null);
  const [headerHeight, setHeaderHeight] = useState(112);

  useEffect(() => {
    const header = headerRef.current;
    if (!header) return undefined;

    const updateHeaderHeight = () => {
      const nextHeight = Math.ceil(header.getBoundingClientRect().height);
      if (nextHeight > 0) setHeaderHeight(nextHeight);
    };

    updateHeaderHeight();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateHeaderHeight);
      return () => window.removeEventListener('resize', updateHeaderHeight);
    }

    const observer = new ResizeObserver(updateHeaderHeight);
    observer.observe(header);
    return () => observer.disconnect();
  }, []);

  const handleTTSToggle = useCallback(() => {
    if (isTTSAutoEnabled) {
      // Disable auto-read mode
      setIsTTSAutoEnabled(false);
      stop();
    } else {
      // Enable auto-read mode
      setIsTTSAutoEnabled(true);
      
      // Play a short silent utterance to unlock speech synthesis on iOS/Safari/macOS
      if (typeof window !== 'undefined' && window.speechSynthesis) {
        const unlockUtterance = new SpeechSynthesisUtterance('');
        window.speechSynthesis.speak(unlockUtterance);
      }

      // If no item is selected, automatically select the first one!
      let targetIndex = childClicked;
      if (targetIndex === null && displayedItems && displayedItems.length > 0) {
        targetIndex = 0;
        setChildClicked(0);
      }

      const focusedItem = displayedItems[targetIndex];
      if (focusedItem) {
        const text = formatEventForTTS(focusedItem, language);
        speak(text);
      }
    }
  }, [isTTSAutoEnabled, stop, speak, displayedItems, childClicked, language, setChildClicked]);

  const handleTTSRestart = useCallback(() => {
    if (!isTTSAutoEnabled) return;
    const idx = childClicked !== null ? childClicked : 0;
    const focusedItem = displayedItems[idx];
    if (focusedItem) {
      const text = formatEventForTTS(focusedItem, language);
      speak(text);
    }
  }, [isTTSAutoEnabled, displayedItems, childClicked, language, speak]);


  // Refs so the auto-advance effect always sees the latest values without
  // adding them as dependencies (which would re-trigger on every scroll).
  const childClickedAutoRef    = useRef(childClicked);
  const displayedItemsAutoRef  = useRef(displayedItems);
  useEffect(() => { childClickedAutoRef.current   = childClicked;   }, [childClicked]);
  useEffect(() => { displayedItemsAutoRef.current = displayedItems; }, [displayedItems]);

  // When reading finishes naturally (not stopped by the user), advance to
  // the next item. SimpleHorizontalList watches childClicked and scrolls
  // automatically, and the existing auto-read effect below re-reads it.
  useEffect(() => {
    if (readingFinished === 0 || !isTTSAutoEnabled) return;

    const current  = childClickedAutoRef.current !== null ? childClickedAutoRef.current : 0;
    const next     = current + 1;
    const items    = displayedItemsAutoRef.current;

    if (next < items.length) {
      setChildClicked(next);
    } else {
      // Reached end of list — turn off auto-read
      setIsTTSAutoEnabled(false);
    }
  }, [readingFinished, isTTSAutoEnabled, setChildClicked]);

  const previousLanguageRef = useRef(language);

  // Auto-read new middle item when scrolling or when language changes (if TTS is enabled)
  useEffect(() => {
    // Skip on initial mount or when childClicked becomes null
    if (previousChildClickedRef.current === null && childClicked === null) {
      return;
    }

    const languageChanged = previousLanguageRef.current !== language;
    previousLanguageRef.current = language;

    // Auto-read if TTS mode is enabled AND (childClicked changed OR language changed)
    if (isTTSAutoEnabled && (childClicked !== previousChildClickedRef.current || languageChanged)) {
      previousChildClickedRef.current = childClicked;

      // Stop current reading
      stop();

      // Start reading current item
      if (childClicked !== null && displayedItems[childClicked]) {
        const focusedItem = displayedItems[childClicked];
        const text = formatEventForTTS(focusedItem, language);
        speak(text);
      }
    } else {
      previousChildClickedRef.current = childClicked;
    }
  }, [childClicked, isTTSAutoEnabled, displayedItems, language, speak, stop]);

  const isTTSDisabled = !isSupported || !displayedItems || displayedItems.length === 0;

  const copy = {
    TH: {
      greeting: 'อรุณสวัสดิ์, Alex!',
      headline: 'กรุงเทพฯ คึกคักวันนี้!',
      countSuffix: 'รายการน่าสนใจตอนนี้',
      mood: 'อยากทำอะไร?',
      eat: 'กิน',
      vibe: 'บรรยากาศ',
      explore: 'สำรวจ',
      more: 'เพิ่มเติม',
      busyTitle: 'ย่านคึกคัก',
      busyBody: 'ย่านนี้มีคนเยอะตอนนี้',
      sheetTitle: 'มีอะไรน่าสนใจตอนนี้',
      seeAll: 'ดูทั้งหมด',
      search: 'ค้นหากิจกรรม',
      filters: 'ตัวกรอง'
    },
    CN: {
      greeting: '早上好, Alex!',
      headline: '曼谷今天很热闹!',
      countSuffix: '个当前推荐',
      mood: '你现在想做什么?',
      eat: '吃',
      vibe: '氛围',
      explore: '探索',
      more: '更多',
      busyTitle: '热门区域',
      busyBody: '这个区域现在很热闹',
      sheetTitle: '现在有什么有趣的',
      seeAll: '查看全部',
      search: '搜索活动',
      filters: '筛选'
    },
    EN: {
      greeting: 'Good morning, Alex!',
      headline: 'Bangkok is buzzing today!',
      countSuffix: 'interesting now',
      mood: 'What are you in the mood for?',
      eat: 'Eat',
      vibe: 'Vibe',
      explore: 'Explore',
      more: 'More',
      busyTitle: 'Busy area',
      busyBody: 'This area is very busy now',
      sheetTitle: "What's interesting now",
      seeAll: 'See all',
      search: 'Search events',
      filters: 'Filters'
    }
  }[language] || {};

  // The mobile chips keep their own look, but the tabs themselves come from
  // NAV_TABS so the selection matches the desktop navigation bar exactly.
  const CHIP_STYLE = {
    Explore:   { icon: <ExploreIcon fontSize="small" />,      color: '#20a47a' },
    Events:    { icon: <MusicNoteIcon fontSize="small" />,     color: '#e83e8c' },
    Reviews:   { icon: <RestaurantIcon fontSize="small" />,    color: '#ff7a1a' },
    Reviewers: { icon: <PersonSearchIcon fontSize="small" />,  color: '#7c3aed' },
    Places:    { icon: <AppsIcon fontSize="small" />,          color: '#202124' },
    Promotion: { icon: <LocalOfferIcon fontSize="small" />,    color: '#0ea5e9' },
  };

  const chipItems = NAV_TABS.map((tab) => ({
    id: tab.id,
    label: tab.label[language] || tab.label.EN,
    icon: CHIP_STYLE[tab.id]?.icon || <AppsIcon fontSize="small" />,
    color: CHIP_STYLE[tab.id]?.color || '#202124',
  }));

  return (
    <Box className="mobile-app-viewport" sx={{
      width: '100%',
      overflow: 'hidden',
      backgroundColor: '#dbe8f0',
      color: '#111827'
    }}>
      {showMobileMenu && (
        <Box sx={{
          position: 'absolute',
          inset: 0,
          zIndex: 5000,
          backgroundColor: '#fff',
          pt: 'var(--safe-area-top)'
        }}>
          <MobileMenuPanel
            onClose={() => setShowMobileMenu(false)}
            onNavigate={handleMobileMenuNavigate}
            isAuthenticated={isAuthenticated}
            onSearch={onSearch}
            logout={logout}
          />
        </Box>
      )}

      <Box sx={{
        position: 'absolute',
        left: 0,
        right: 0,
        top: isVerticalExplore ? `calc(100% - (${visibleExploreMapHeight} + var(--mobile-bottom-nav-height)))` : 0,
        bottom: isVerticalExplore ? 'var(--mobile-bottom-nav-height)' : 0,
        // Own stacking context so the map's own controls (z-index:1000) stay
        // BELOW the saved / profile panels (z-index:20) instead of poking through.
        zIndex: 0,
      }}>
        <Map
          setCoordinates={setCoordinates}
          setBounds={setBounds}
          bounds={bounds}
          coordinates={coordinates}
          places={savedShowMarkers ? savedPanel.mapItems : (profileMarkersActive ? myWorksMapItems : displayedItems)}
          selectedEventId={(savedShowMarkers || profileMarkersActive) ? null : selectedEventId}
          onMarkerClick={handleMarkerClick}
          userLocation={userLocation}
          onSearch={onSearch}
          language={language}
          controlsBottomOffset={mapControlsBottom}
          disablePopups={isVerticalExplore}
          onInfoBoxClick={handleInfoBoxClick}
          onCreatorClick={onCreatorClick}
          fitToPlacesKey={savedShowMarkers
            ? savedPanel.mapFitKey
            : profileMarkersActive
              ? myWorksFitKey
              : (isReviewerTab && selectedCreator?.id ? `reviewer-${selectedCreator.id}` : null)}
        />
      </Box>

      <Box sx={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        background: 'linear-gradient(180deg, rgba(255,255,255,0.76) 0%, rgba(255,255,255,0.18) 13%, rgba(255,255,255,0) 30%)'
      }} />

      <Box ref={headerRef} sx={{
        position: 'absolute',
        top: 'calc(var(--safe-area-top) + 10px)',
        left: 'max(12px, var(--safe-area-left))',
        right: 'max(12px, var(--safe-area-right))',
        zIndex: 20
      }}>
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: '34px auto minmax(0, 1fr) 40px',
          gap: 0.55,
          alignItems: 'center',
          py: 0.7,
          pl: 0.75,
          pr: 0.6,
          minHeight: 50,
          borderRadius: '18px',
          backgroundColor: 'rgba(255,255,255,0.9)',
          backdropFilter: 'blur(14px)',
          boxShadow: '0 10px 26px rgba(31, 41, 55, 0.12)',
          border: '1px solid rgba(255,255,255,0.72)'
        }}>
          <IconButton
            onClick={() => setShowMobileMenu(true)}
            size="small"
            aria-label="Open menu"
            sx={{
              width: 34,
              height: 34,
              color: '#59627d',
              backgroundColor: 'transparent',
              '&:hover': { backgroundColor: 'rgba(17,24,39,0.06)' }
            }}
          >
            <MenuIcon fontSize="small" />
          </IconButton>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            '& .MuiIconButton-root': {
              width: 34,
              height: 34,
              backgroundColor: 'rgba(17,24,39,0.06)'
            },
            '& svg': { fontSize: 20 }
          }}>
            <TTSButton
              isPlaying={isTTSAutoEnabled}
              isDisabled={isTTSDisabled}
              onToggle={handleTTSToggle}
              audioOutputDevices={audioOutputDevices}
              selectedOutputDeviceId={selectedOutputDeviceId}
              onOutputDeviceChange={setOutputDevice}
              onRestart={handleTTSRestart}
            />
          </Box>
          <Box sx={{ position: 'relative', minWidth: 0 }}>
            <TextField
              value={searchTerm || ''}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                if (e.target.value === '') onSearch('');
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') onSearch(searchTerm || '');
              }}
              placeholder={language === 'TH' ? 'ค้นหา' : language === 'CN' ? '搜索' : 'Search'}
              size="small"
              variant="standard"
              InputProps={{ disableUnderline: true }}
              sx={{
                minWidth: 0,
                width: '100%',
                '& input': {
                  fontWeight: 800,
                  fontSize: 15,
                  py: 0.5,
                  pr: searchTerm ? '22px' : 0,
                },
                '& input::placeholder': {
                  color: '#4b5563',
                  opacity: 1,
                  fontWeight: 700,
                }
              }}
            />
            {searchTerm && (
              <IconButton
                size="small"
                onClick={() => { setSearchTerm(''); onSearch(''); }}
                sx={{
                  position: 'absolute',
                  right: 0,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#888',
                  padding: '2px',
                }}
                aria-label="Clear search"
              >
                <CloseIcon sx={{ fontSize: 16 }} />
              </IconButton>
            )}
          </Box>
          <IconButton
            onClick={() => setIsSearchExpanded((prev) => !prev)}
            aria-label={isSearchExpanded ? 'Hide search filters' : 'Show search filters'}
            sx={{
              width: 40,
              height: 40,
              backgroundColor: '#fff',
              color: '#111827',
              boxShadow: '0 8px 18px rgba(17, 24, 39, 0.13)',
              '&:hover': { backgroundColor: '#fff' }
            }}
          >
            <KeyboardArrowDownIcon
              sx={{
                fontSize: 28,
                transform: isSearchExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 160ms ease'
              }}
            />
          </IconButton>
        </Box>

        <Collapse in={isSearchExpanded}>
          <Box sx={{
            mt: 1,
            p: 1,
            borderRadius: '18px',
            backgroundColor: 'rgba(255,255,255,0.92)',
            boxShadow: '0 12px 30px rgba(31,41,55,0.14)',
            pointerEvents: 'auto'
          }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr) 42px', gap: 0.65, alignItems: 'center' }}>
              <TextField
                type="date"
                value={startDate || ''}
                onChange={(e) => setStartDate(e.target.value)}
                size="small"
                sx={{
                  minWidth: 0,
                  '& .MuiInputBase-root': { height: 42, borderRadius: '14px', backgroundColor: '#fff' },
                  '& input': { px: 1.1, py: 0, fontSize: 14, fontWeight: 500, minWidth: 0 }
                }}
              />
              <TextField
                type="date"
                value={endDate || ''}
                onChange={(e) => setEndDate(e.target.value)}
                size="small"
                sx={{
                  minWidth: 0,
                  '& .MuiInputBase-root': { height: 42, borderRadius: '14px', backgroundColor: '#fff' },
                  '& input': { px: 1.1, py: 0, fontSize: 14, fontWeight: 500, minWidth: 0 }
                }}
              />
              <IconButton onClick={() => onSearch(searchTerm || '')} sx={{ width: 42, height: 42, backgroundColor: '#6d4cff', color: '#fff', borderRadius: '14px', '&:hover': { backgroundColor: '#5b3bea' } }}>
                <SearchIcon />
              </IconButton>
            </Box>
          </Box>
        </Collapse>

        {/* Category tabs — hidden in any transparent map-only overlay */}
        <Box sx={{
          display: mapOnlyView ? 'none' : 'flex',
          gap: 0.75,
          overflowX: 'auto',
          mt: 0.85,
          pb: 0.2,
          pointerEvents: 'auto',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' }
        }}>
          {chipItems.map((chip) => {
            const isActive = type === chip.id;
            return (
              <Box
                key={chip.id}
                onClick={() => {
                  setType(chip.id);
                  if (setSubCategory) setSubCategory('');
                }}
                sx={{
                  flex: '0 0 auto',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.55,
                  px: 1.25,
                  py: 0.85,
                  minWidth: 76,
                  borderRadius: 999,
                  backgroundColor: isActive ? 'rgba(255,255,255,0.96)' : 'rgba(255,255,255,0.82)',
                  color: '#111827',
                  border: isActive ? `1.5px solid ${chip.color}` : '1px solid rgba(17,24,39,0.08)',
                  boxShadow: isActive ? '0 8px 20px rgba(17,24,39,0.12)' : '0 5px 14px rgba(17,24,39,0.08)',
                  backdropFilter: 'blur(12px)',
                  cursor: 'pointer',
                }}
              >
                <Box sx={{ color: chip.color, display: 'grid' }}>
                  {chip.icon}
                </Box>
                <Typography sx={{ fontWeight: 850, fontSize: 12, lineHeight: 1 }}>
                  {chip.label}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Box>

      <Box sx={{
        position: 'absolute',
        left: 0,
        right: 0,
        overflow: 'hidden',
        pointerEvents: 'auto',
        transition: 'height 200ms ease, bottom 200ms ease',
        // Hidden under any transparent map-only overlay — otherwise this
        // recommendation sheet (normal feed) shows through.
        display: mapOnlyView ? 'none' : 'block',
        ...(isVerticalExplore ? {
          top: 0,
          // Expanded = the list covers the map strip as well
          bottom: isListExpanded
            ? 'var(--mobile-bottom-nav-height)'
            : `calc(var(--mobile-bottom-nav-height) + ${visibleExploreMapHeight})`,
          zIndex: 17,
          // The measured clearance adapts when search filters or text size change.
          pt: `calc(var(--safe-area-top) + ${headerHeight + 10}px)`,
          pb: `${listToggleHeight}px`,
          backgroundColor: 'rgba(255,255,255,0.96)',
          boxShadow: '0 12px 34px rgba(17,24,39,0.16)',
        } : {
          bottom: 'var(--mobile-bottom-nav-height)',
          zIndex: 16,
          // Collapsed = only the title row stays on screen
          height: isSheetOpen ? 'min(36vh, 320px)' : `${sheetHeaderHeight}px`,
          minHeight: isSheetOpen ? recommendationSheetHeight : sheetHeaderHeight,
          borderRadius: '20px 20px 0 0',
          backgroundColor: 'rgba(255,255,255,0.94)',
          boxShadow: '0 -10px 30px rgba(31,41,55,0.14)',
          pt: 0,
        })
      }}
        onWheel={() => setFollowList(true)}
        onTouchStart={() => setFollowList(true)}
      >
        {!isVerticalExplore && <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 1.5,
          pt: 0.55,
          pb: 0.25,
          height: sheetHeaderHeight,
          boxSizing: 'border-box',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
            <LocalFireDepartmentIcon sx={{ color: '#ff4d00', fontSize: 18 }} />
            <Typography sx={{ fontWeight: 850, fontSize: 15, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {copy.sheetTitle}
            </Typography>
          </Box>

          {/* Hide / show this sheet */}
          <IconButton
            size="small"
            onClick={() => setIsSheetOpen((prev) => !prev)}
            aria-label={isSheetOpen ? 'Hide recommendations' : 'Show recommendations'}
            sx={{
              flexShrink: 0,
              width: 28,
              height: 28,
              color: '#5f6675',
              backgroundColor: 'rgba(17,24,39,0.06)',
              '&:hover': { backgroundColor: 'rgba(124,58,237,0.12)' },
            }}
          >
            <KeyboardArrowDownIcon sx={{
              fontSize: 20,
              transform: isSheetOpen ? 'rotate(0deg)' : 'rotate(180deg)',
              transition: 'transform 160ms ease',
            }} />
          </IconButton>
        </Box>}

        {isReviewerTab ? (
          <Box sx={{ height: isVerticalExplore ? '100%' : 'calc(100% - 27px)' }}>
            <ReviewerPanel
              selectedReviewer={selectedCreator}
              onSelectReviewer={onReviewerSelect || setSelectedCreator}
              onClearReviewer={() => setSelectedCreator(null)}
              profile={creatorProfile}
              language={language}
              layout={isVerticalExplore ? 'vertical' : 'horizontal'}
              childClicked={childClicked}
              onLocationClick={handleLocationClick}
              onActiveIndexChange={handleActiveIndexChange}
              onItemsChange={handleItemsChange}
            />
          </Box>
        ) : (
        <Box sx={{ height: isVerticalExplore ? '100%' : 'calc(100% - 27px)' }}>
          <SmartEventList
            layout={isVerticalExplore ? 'vertical' : 'horizontal'}
            context="explore"
            feedMode={true}
            enabled={feedLat !== null && feedLat !== undefined}
            language={language}
            userLocation={userLocation}
            lat={feedLat}
            lng={feedLng}
            radiusKm={feedRadiusKm ?? undefined}
            searchQuery={searchQuery}
            category={categoryFilter || undefined}
            source={source}
            excludeCategories={excludeCategories}
            startDate={startDate}
            endDate={endDate}
            childClicked={childClicked}
            onLocationClick={handleLocationClick}
            onActiveIndexChange={handleActiveIndexChange}
            onItemsChange={handleItemsChange}
            onMapSyncNeeded={handleMapSync}
            showPager={false}
            hasSearched={hasSearched}
            expandedPlace={expandedPlace}
            onExpand={(place) => setExpandedPlace(place)}
            onExpandClose={() => setExpandedPlace(null)}
            onCreatorClick={onCreatorClick} // Reviewer name on a card → ผู้รีวิว tab
          />
        </Box>
        )}

        {/* รายการ view: expand the list over the map strip, or shrink it back */}
        {isVerticalExplore && (
          <Box
            onClick={() => setIsListExpanded((prev) => !prev)}
            role="button"
            aria-label={isListExpanded ? 'Shrink list' : 'Expand list over map'}
            sx={{
              position: 'absolute',
              left: 0,
              right: 0,
              bottom: 0,
              height: `${listToggleHeight}px`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.5,
              cursor: 'pointer',
              backgroundColor: 'rgba(255,255,255,0.97)',
              borderTop: '1px solid rgba(17,24,39,0.08)',
              boxShadow: '0 -4px 14px rgba(17,24,39,0.08)',
              color: '#5f6675',
              zIndex: 2,
              '&:active': { backgroundColor: 'rgba(124,58,237,0.08)' },
            }}
          >
            <KeyboardArrowDownIcon sx={{
              fontSize: 18,
              transform: isListExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 160ms ease',
            }} />
            <Typography sx={{ fontSize: 11, fontWeight: 700 }}>
              {isListExpanded
                ? (language === 'TH' ? 'ย่อรายการ' : language === 'CN' ? '收起列表' : 'Shrink list')
                : (language === 'TH' ? 'ขยายรายการ' : language === 'CN' ? '展开列表' : 'Expand list')}
            </Typography>
          </Box>
        )}
      </Box>

      {showSavedPanel && (
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 'var(--mobile-bottom-nav-height)',
          zIndex: 20,
          // On the แผนที่ tab the panel goes transparent and click-through so the
          // already-loaded map underneath shows and stays interactive.
          backgroundColor: savedTab === 'map' ? 'transparent' : '#f9fafb',
          pointerEvents: savedTab === 'map' ? 'none' : 'auto',
          display: 'flex',
          flexDirection: 'column',
          pt: 'var(--safe-area-top)'
        }}>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            py: 1.5,
            bgcolor: '#fff',
            boxShadow: '0 1px 4px rgba(0,0,0,.08)',
            flexShrink: 0,
            pointerEvents: 'auto'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton size="small" onClick={() => setShowSavedPanel(false)}>
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="h6" sx={{ fontWeight: 800, color: 'text.primary', fontSize: '1.1rem' }}>
                {language === 'TH' ? 'ที่บันทึกไว้' : language === 'CN' ? '已保存' : 'Saved'}
              </Typography>
            </Box>

            {/* Segmented Control Selector Tabs: ตาราง / แผนที่ / ติดตาม */}
            <Box sx={{
              display: 'flex',
              backgroundColor: '#f1f5f9',
              borderRadius: '20px',
              p: 0.5,
              border: '1px solid #e2e8f0'
            }}>
              {[
                { id: 'grid', label: language === 'TH' ? 'ตาราง' : language === 'CN' ? '网格' : 'Grid' },
                { id: 'map', label: language === 'TH' ? 'แผนที่' : language === 'CN' ? '地图' : 'Map' },
                { id: 'following', label: language === 'TH' ? 'ติดตาม' : language === 'CN' ? '关注' : 'Following' },
              ].map((tab) => (
                <Button
                  key={tab.id}
                  size="small"
                  onClick={() => savedPanel.changeTab(tab.id)}
                  sx={{
                    borderRadius: '16px',
                    fontSize: '0.76rem',
                    fontWeight: 750,
                    textTransform: 'none',
                    py: 0.4,
                    px: 1.6,
                    minWidth: 0,
                    color: savedTab === tab.id ? '#ffffff' : '#475569',
                    backgroundColor: savedTab === tab.id ? '#7c3aed' : 'transparent',
                    boxShadow: savedTab === tab.id ? '0 2px 8px rgba(124, 58, 237, 0.25)' : 'none',
                    '&:hover': {
                      backgroundColor: savedTab === tab.id ? '#6d28d9' : 'rgba(0,0,0,0.04)'
                    },
                    transition: 'all 0.2s'
                  }}
                >
                  {tab.label}
                </Button>
              ))}
            </Box>
          </Box>

          {/* Reviewer context banner — which followed reviewer we're showing */}
          {savedPanel.selectedReviewer && (
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              px: 2,
              py: 1,
              bgcolor: 'rgba(124, 58, 237, 0.06)',
              borderBottom: '1px solid rgba(124, 58, 237, 0.15)',
              flexShrink: 0,
              pointerEvents: 'auto'
            }}>
              <Typography sx={{ fontSize: '0.82rem', color: '#6d28d9', fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {language === 'TH' ? 'กำลังแสดงรายการของ ' : language === 'CN' ? '正在显示 ' : 'Showing items by '}
                {savedPanel.selectedReviewer.name}
              </Typography>
              <Button
                size="small"
                onClick={() => savedPanel.clearReviewer()}
                startIcon={<CloseIcon sx={{ fontSize: 16 }} />}
                sx={{
                  flexShrink: 0,
                  borderRadius: '16px',
                  fontSize: '0.72rem',
                  fontWeight: 700,
                  textTransform: 'none',
                  color: '#7c3aed',
                  '&:hover': { backgroundColor: 'rgba(124, 58, 237, 0.08)' }
                }}
              >
                {language === 'TH' ? 'กลับไปของฉัน' : language === 'CN' ? '返回我的' : 'Back to mine'}
              </Button>
            </Box>
          )}

          <Box sx={{
            flex: 1,
            overflowY: savedTab === 'map' ? 'hidden' : 'auto',
            pointerEvents: savedTab === 'map' ? 'none' : 'auto'
          }}>
            {savedTab === 'grid' ? (
              savedPanel.detailItem ? (
                <SavedItemDetail
                  item={savedPanel.detailItem}
                  language={language}
                  onBack={savedPanel.closeDetail}
                  onViewOnMap={(lat, lng) => { setCoordinates({ lat, lng }); savedPanel.closeDetail(); setSavedTab('map'); }}
                />
              ) : (
                <SavedItemsGrid
                  items={savedPanel.gridItems}
                  loading={savedPanel.gridLoading}
                  language={language}
                  onItemClick={handleSavedGridItemClick}
                  emptyText={savedPanel.selectedReviewer
                    ? (language === 'TH' ? 'ยังไม่มีรายการของผู้รีวิวคนนี้' : language === 'CN' ? '该评论者暂无内容' : 'This reviewer has no items yet')
                    : undefined}
                />
              )
            ) : savedTab === 'map' ? (
              <Box sx={{ height: '100%', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', pt: 1.5 }}>
                {savedPanel.mapItems.length === 0 && !savedPanel.gridLoading && (
                  <Box sx={{
                    px: 2, py: 1,
                    borderRadius: '14px',
                    backgroundColor: 'rgba(255,255,255,0.92)',
                    boxShadow: '0 4px 14px rgba(17,24,39,0.12)'
                  }}>
                    <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary', fontWeight: 600 }}>
                      {language === 'TH' ? 'ไม่มีตำแหน่งที่จะแสดงบนแผนที่' : language === 'CN' ? '没有可显示的位置' : 'No locations to show on the map'}
                    </Typography>
                  </Box>
                )}
              </Box>
            ) : (
              <FollowedReviewersList
                creators={savedPanel.followedCreators}
                loading={savedPanel.followedLoading}
                language={language}
                onSelect={(creator) => savedPanel.selectReviewer(creator)}
              />
            )}
          </Box>
        </Box>
      )}


      {showProfilePanel && (
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 'var(--mobile-bottom-nav-height)',
          zIndex: 20,
          // Map view: transparent + click-through so the map (with the selected
          // category's markers) shows underneath.
          backgroundColor: profileView === 'map' ? 'transparent' : '#f9fafb',
          pointerEvents: profileView === 'map' ? 'none' : 'auto',
          display: 'flex',
          flexDirection: 'column',
          pt: 'var(--safe-area-top)'
        }}>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            px: 2,
            py: 1.5,
            bgcolor: '#fff',
            boxShadow: '0 1px 4px rgba(0,0,0,.08)',
            flexShrink: 0,
            pointerEvents: 'auto'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
              <IconButton size="small" onClick={() => setShowProfilePanel(false)}>
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="h6" sx={{ fontWeight: 800, color: 'text.primary', fontSize: '1.1rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {language === 'CN' ? '我的作品' : language === 'EN' ? 'My Works' : 'ผลงานของฉัน'}
              </Typography>
            </Box>

            {/* Grid / Map view toggle */}
            <Box sx={{ display: 'flex', backgroundColor: '#f1f5f9', borderRadius: '20px', p: 0.5, border: '1px solid #e2e8f0', flexShrink: 0 }}>
              {[
                { id: 'grid', icon: <AppsIcon sx={{ fontSize: 18 }} /> },
                { id: 'map', icon: <MapOutlinedIcon sx={{ fontSize: 18 }} /> },
              ].map((v) => (
                <IconButton
                  key={v.id}
                  size="small"
                  onClick={() => setProfileView(v.id)}
                  aria-label={v.id === 'map' ? 'Map view' : 'Grid view'}
                  sx={{
                    borderRadius: '14px',
                    p: 0.6,
                    color: profileView === v.id ? '#fff' : '#475569',
                    backgroundColor: profileView === v.id ? '#7c3aed' : 'transparent',
                    '&:hover': { backgroundColor: profileView === v.id ? '#6d28d9' : 'rgba(0,0,0,0.04)' },
                    transition: 'all 0.2s',
                  }}
                >
                  {v.icon}
                </IconButton>
              ))}
            </Box>
          </Box>

          <Box sx={{
            flex: 1,
            overflowY: profileView === 'map' ? 'hidden' : 'auto',
            p: profileView === 'map' ? 0 : 1,
            // Map view: click-through to the map, except the category tabs
            // (which re-enable their own pointer events).
            pointerEvents: profileView === 'map' ? 'none' : 'auto',
          }}>
            <MyWorksPanel
              language={language}
              refreshKey={showProfilePanel ? 1 : 0}
              onMapItemsChange={handleMyWorksMapItems}
              view={profileView}
            />
            {profileView === 'map' && myWorksMapItems.length === 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', pt: 2 }}>
                <Box sx={{ px: 2, py: 1, borderRadius: '14px', backgroundColor: 'rgba(255,255,255,0.92)', boxShadow: '0 4px 14px rgba(17,24,39,0.12)' }}>
                  <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary', fontWeight: 600 }}>
                    {language === 'TH' ? 'ไม่มีตำแหน่งที่จะแสดงบนแผนที่' : language === 'CN' ? '没有可显示的位置' : 'No locations to show on the map'}
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      )}

      <Box sx={{
        position: 'absolute',
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 25,
        pointerEvents: 'auto'
      }}>
        <BottomNavigation
          mode={currentView}
          onHome={() => {
            setCurrentView('home');
            setShowSavedPanel(false);
            setShowProfilePanel(false);
          }}
          onExplore={() => {
            setCurrentView('verticalExplore');
            setShowSavedPanel(false);
            setShowProfilePanel(false);
          }}
          onShowSaved={() => {
            if (!isAuthenticated) { goToLoginForPanel('saved'); return; }
            setShowSavedPanel(true);
            setShowProfilePanel(false);
          }}
          savedActive={showSavedPanel}
          onShowProfile={() => {
            if (!isAuthenticated) { goToLoginForPanel('profile'); return; }
            setProfileView('grid');
            setShowSavedPanel(false);
            setShowProfilePanel(true);
          }}
        />
      </Box>
    </Box>
  );
};

export default MobileLayout;
